import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import type { AIModel } from '@/api/types'
import { ProtoDropdown } from '@/features/studio/components/ui-kit'

export type ScriptAIModelFieldProps = {
  disabled?: boolean
  modelId: string
  setModelId: (id: string) => void
  textModels: AIModel[]
  selectedModel?: AIModel
  loading: boolean
  ready: boolean
  compact?: boolean
  inline?: boolean
  trailing?: ReactNode
  /** 底部工具栏等场景：下拉挂 body 并向上展开 */
  dropdownPortal?: boolean
}

export function ScriptAIModelField({
  disabled,
  modelId,
  setModelId,
  textModels,
  selectedModel,
  loading,
  ready,
  compact,
  inline,
  trailing,
  dropdownPortal,
}: ScriptAIModelFieldProps) {
  const { t } = useTranslation()

  if (!loading && textModels.length === 0) {
    return (
      <p className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-2.5 py-2 text-[11px] text-amber-200/90">
        {t('script.aiModelEmpty')}
      </p>
    )
  }

  if (inline) {
    return (
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="relative z-20 flex items-center gap-2 overflow-visible">
          <div className="relative min-w-0 flex-1 overflow-visible">
            <ProtoDropdown
              compact
              placement="top"
              portal={dropdownPortal}
              value={modelId}
              onValueChange={setModelId}
              disabled={disabled || loading || !ready}
              placeholder={loading ? t('script.aiModelLoading') : t('script.aiModelPlaceholder')}
              options={textModels.map((m) => ({
                value: m.id,
                label: `${m.name} · ${t('script.aiModelCostUnit', { cost: m.cost })}`,
              }))}
            />
          </div>
          {trailing}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-1.5">
      <p className="text-[10px] font-medium uppercase tracking-wide text-zinc-600">
        {t('script.aiModelLabel')}
      </p>
      <ProtoDropdown
        value={modelId}
        onValueChange={setModelId}
        disabled={disabled || loading || !ready}
        placeholder={loading ? t('script.aiModelLoading') : t('script.aiModelPlaceholder')}
        options={textModels.map((m) => ({
          value: m.id,
          label: `${m.name} · ${t('script.aiModelCostUnit', { cost: m.cost })}`,
        }))}
      />
      {selectedModel && !compact ? (
        <p className="text-[10px] text-zinc-600">
          {t('script.aiModelCostHint', { cost: selectedModel.cost })}
        </p>
      ) : null}
    </div>
  )
}
